// The website's UI components on Bootstrap (react-bootstrap). Pages import only from '@ui' (a Vite
// alias for this folder) and never name a framework class: another framework is tried by writing
// the same exports in a sibling folder and pointing the alias at it (docs/website/plan.md, "A more
// modern UI framework", stage 0). Every component takes the style props described in sx.js.
import {
  Alert as BsAlert, Badge as BsBadge, Button as BsButton, ButtonGroup as BsButtonGroup, Card as BsCard,
  Col as BsCol, Container as BsContainer, Dropdown, Form as BsForm, InputGroup, Modal as BsModal, Nav,
  Navbar, Pagination as BsPagination, ProgressBar as BsProgressBar, Row, Spinner as BsSpinner,
  Table as BsTable, ToggleButton, ToggleButtonGroup,
} from 'react-bootstrap'
import { Link, NavLink } from 'react-router-dom'
import { cx, sx } from './sx.js'

// Light or dark for the whole page. data-wl-theme is ours (index.css keys the chart colours off
// it); data-bs-theme is what Bootstrap reads.
export function setColorMode(mode) {
  document.documentElement.dataset.wlTheme = mode
  document.documentElement.dataset.bsTheme = mode
}

// ---- Layout and text ----

// Any element with the style props: <Box as="ul" unstyledList mb={2}>.
export function Box({ as: Tag = 'div', ...props }) {
  const [className, rest] = sx(props)
  return <Tag className={className} {...rest} />
}

// A flex row (or column) with gap and alignment: <Stack gap={2} align="center" wrap>.
export function Stack({ as: Tag = 'div', inline = false, ...props }) {
  const [className, rest] = sx({ display: inline ? 'inline-flex' : 'flex', ...props })
  return <Tag className={className} {...rest} />
}

// Inline text with the style props: <Text muted text="small">.
export function Text({ as: Tag = 'span', ...props }) {
  const [className, rest] = sx(props, { display: 'inline' })
  return <Tag className={className} {...rest} />
}

// A heading at its document level, drawn at another size: <Heading level={2} size={5}>.
export function Heading({ level = 1, size = level, ...props }) {
  const [className, rest] = sx({ ...props, className: cx(size !== level && `h${size}`, props.className) })
  const Tag = `h${level}`
  return <Tag className={className} {...rest} />
}

export function VisuallyHidden({ children }) {
  return <span className="visually-hidden">{children}</span>
}

// A link: to= for the app's own pages (router), href= for anything else.
export function TextLink({ to, ...props }) {
  const [className, rest] = sx(props)
  return to != null ? <Link to={to} className={className} {...rest} /> : <a className={className} {...rest} />
}

export function Container({ as, ...props }) {
  const [className, rest] = sx(props)
  return <BsContainer as={as} className={className} {...rest} />
}

// A responsive grid: <Grid gap={3}><Col xs={6} lg={2}>…</Col></Grid>. Column spans out of 12.
export function Grid({ gap, ...props }) {
  const [className, rest] = sx({ ...props, className: cx(gap != null && `g-${gap}`, props.className) })
  return <Row className={className} {...rest} />
}

export function Col({ xs, sm, md, lg, ...props }) {
  const [className, rest] = sx(props)
  return <BsCol xs={xs} sm={sm} md={md} lg={lg} className={className} {...rest} />
}

// ---- Feedback ----

export function Spinner({ size, label = 'Loading…', ...props }) {
  const [className] = sx(props)
  return (
    <BsSpinner animation="border" size={size} role="status" className={className}>
      <span className="visually-hidden">{label}</span>
    </BsSpinner>
  )
}

// variant: 'danger' | 'warning' | 'info' | 'success'. compact: less padding.
export function Alert({ variant = 'info', compact = false, ...props }) {
  const [className, rest] = sx({ ...props, className: cx(compact && 'py-2', props.className) })
  return <BsAlert variant={variant} className={className} {...rest} />
}

// variant: 'neutral' | 'success'.
export function Badge({ variant = 'neutral', ...props }) {
  const [className, rest] = sx(props)
  return <BsBadge bg={variant === 'neutral' ? 'secondary' : variant} className={className} {...rest} />
}

// value: 0–100. thin: a hairline under a line of text.
export function ProgressBar({ value, thin = false }) {
  return <BsProgressBar now={value} style={thin ? { height: '0.25rem' } : undefined} />
}

// ---- Buttons ----

// variant: 'primary' | 'secondary' | 'danger' | 'link' | 'dark'; outline: drawn as an outline;
// onDark: an outline button on a dark surface. as/to/href make it a link.
export function Button({ variant = 'primary', outline = false, onDark = false, size, ...props }) {
  const [className, rest] = sx(props)
  const bsVariant = onDark ? 'outline-light' : outline ? `outline-${variant}` : variant
  return <BsButton variant={bsVariant} size={size} className={className} {...rest} />
}

export function ButtonGroup({ size, ...props }) {
  const [className, rest] = sx(props)
  return <BsButtonGroup size={size} className={className} {...rest} />
}

// One choice of several, as joined buttons: options [{ value, label }].
export function Segmented({ name, value, onChange, options, size, onDark = false, ...rest }) {
  return (
    <ToggleButtonGroup type="radio" name={name} size={size} value={value} onChange={onChange} {...rest}>
      {options.map((o) => (
        <ToggleButton key={o.value} id={`${name}-${o.value}`} value={o.value} variant={onDark ? 'outline-light' : 'outline-secondary'}>
          {o.label}
        </ToggleButton>
      ))}
    </ToggleButtonGroup>
  )
}

// A button opening a menu that stays open while its items are used (e.g. checkboxes).
export function Menu({ label, children, ...props }) {
  const [className, rest] = sx(props)
  return (
    <Dropdown autoClose="outside" className={className} {...rest}>
      <Dropdown.Toggle variant="outline-secondary">{label}</Dropdown.Toggle>
      <Dropdown.Menu className="px-3">{children}</Dropdown.Menu>
    </Dropdown>
  )
}

// Previous / "Page n of m" / next.
export function Pagination({ page, count, onChange }) {
  return (
    <BsPagination size="sm">
      <BsPagination.Prev disabled={page === 1} onClick={() => onChange(page - 1)} />
      <BsPagination.Item disabled>Page {page} of {count}</BsPagination.Item>
      <BsPagination.Next disabled={page === count} onClick={() => onChange(page + 1)} />
    </BsPagination>
  )
}

// ---- Surfaces ----

// A bordered panel; title is a small heading (an h2 unless titleAs says otherwise).
export function Card({ title, titleAs = 'h2', compact = false, children, ...props }) {
  const [className, rest] = sx(props)
  return (
    <BsCard className={className} {...rest}>
      <BsCard.Body className={compact ? 'py-2' : undefined}>
        {title != null && <BsCard.Title as={titleAs} className="h6">{title}</BsCard.Title>}
        {children}
      </BsCard.Body>
    </BsCard>
  )
}

// A dialog. dark: dark whatever the page's mode (the photo viewer). panelClassName: a page class
// for the dialog's box itself.
export function Modal({ show, onHide, fullscreen, dark = false, label, panelClassName, ...props }) {
  const [className, rest] = sx(props)
  return (
    <BsModal
      show={show} onHide={onHide} fullscreen={fullscreen} className={className} contentClassName={panelClassName}
      data-bs-theme={dark ? 'dark' : undefined} aria-label={label} {...rest}
    />
  )
}

export function ModalHeader({ closeButton = true, ...props }) {
  const [className, rest] = sx(props)
  return <BsModal.Header closeButton={closeButton} className={className} {...rest} />
}

// size: drawn at a heading size (an h2 drawn as h5).
export function ModalTitle({ as = 'div', size, ...props }) {
  const [className, rest] = sx({ ...props, className: cx(size && `h${size}`, props.className) })
  return <BsModal.Title as={as} className={className} {...rest} />
}

export function ModalBody(props) {
  const [className, rest] = sx(props)
  return <BsModal.Body className={className} {...rest} />
}

export function ModalFooter(props) {
  const [className, rest] = sx(props)
  return <BsModal.Footer className={className} {...rest} />
}

// The box a chart shows on hover, placed by the chart (style top/left).
export function ChartTip({ style, children }) {
  return (
    <div className="position-absolute bg-body border rounded shadow-sm px-2 py-1 small" style={style}>
      {children}
    </div>
  )
}

// ---- Tables ----

// compact: tighter rows; hover: rows highlight; responsive: scrolls sideways on narrow screens;
// middle: cells centred vertically. autoWidth: only as wide as its content.
export function Table({ compact = true, hover, responsive, middle, ...props }) {
  const [className, rest] = sx({ ...props, className: cx(middle && 'align-middle', props.className) })
  return <BsTable size={compact ? 'sm' : undefined} hover={hover} responsive={responsive} className={className} {...rest} />
}

// numeric: right-aligned. active: highlighted (a row).
export function Tr({ active = false, ...props }) {
  const [className, rest] = sx({ ...props, className: cx(active && 'table-active', props.className) })
  return <tr className={className} {...rest} />
}

export function Th({ numeric = false, ...props }) {
  const [className, rest] = sx({ textAlign: numeric ? 'end' : undefined, ...props }, { display: 'table-cell' })
  return <th className={className} {...rest} />
}

export function Td({ numeric = false, ...props }) {
  const [className, rest] = sx({ textAlign: numeric ? 'end' : undefined, ...props }, { display: 'table-cell' })
  return <td className={className} {...rest} />
}

// ---- Forms ----

export function Form(props) {
  const [className, rest] = sx(props)
  return <BsForm className={className} {...rest} />
}

export function Label(props) {
  const [className, rest] = sx(props)
  return <BsForm.Label className={className} {...rest} />
}

// A text, number, date or search field. size 'sm' for a smaller one. suffix: a unit shown joined
// to its end (then width sets the pair's width).
export function Input({ size, suffix, width, ...props }) {
  const [className, rest] = sx(props)
  if (suffix == null) return <BsForm.Control size={size} className={className} {...rest} />
  return (
    <InputGroup size={size} style={width != null ? { width } : undefined}>
      <BsForm.Control className={className} {...rest} />
      <InputGroup.Text>{suffix}</InputGroup.Text>
    </InputGroup>
  )
}

export function TextArea(props) {
  const [className, rest] = sx(props)
  return <BsForm.Control as="textarea" className={className} {...rest} />
}

export function Select({ size, ...props }) {
  const [className, rest] = sx(props)
  return <BsForm.Select size={size} className={className} {...rest} />
}

// A checkbox with its label; toggle: drawn as a switch.
export function Checkbox({ toggle = false, ...props }) {
  const [className, rest] = sx(props)
  return <BsForm.Check type={toggle ? 'switch' : 'checkbox'} className={className} {...rest} />
}

// ---- The site's navbar ----

// Dark bar with the brand, page links (folded into a menu below sm), tools that stay visible
// (the unit toggle) and an end slot (account) inside the fold.
export function AppNavbar({ brand, links, tools, end }) {
  return (
    <Navbar bg="dark" data-bs-theme="dark" expand="sm" className="mb-4">
      <BsContainer>
        <Navbar.Brand as={Link} to="/">{brand}</Navbar.Brand>
        <div className="d-flex align-items-center gap-2 ms-auto order-sm-last">
          {tools}
          {links.length > 0 && <Navbar.Toggle aria-controls="main-nav" />}
        </div>
        <Navbar.Collapse id="main-nav">
          <Nav className="me-auto">
            {links.map((l) => <Nav.Link key={l.to} as={NavLink} to={l.to}>{l.label}</Nav.Link>)}
          </Nav>
          {end && <span className="d-flex align-items-center gap-2 me-sm-3 my-2 my-sm-0">{end}</span>}
        </Navbar.Collapse>
      </BsContainer>
    </Navbar>
  )
}
